import { baseApi } from './baseApi';

export interface SearchResult {
  id: string;
  entityType: string;
  title: string;
  description?: string;
  /** Wo der Eintrag in der Anwendung liegt — null, wenn es dafür keine Seite gibt. */
  url: string | null;
  score: number;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  totalPages: number;
  /** Ob die Bedeutung mitgesucht hat (K25) — und wenn nicht, warum nicht. */
  semantic: boolean;
  semanticReason: string | null;
}

/** What the service sends: one hit with its stored fields. */
interface RawHit {
  id: string;
  entityType: string;
  score: number;
  highlight?: Record<string, string[]>;
  source?: Record<string, unknown>;
}

interface RawSearchResponse {
  data: RawHit[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  semantic?: boolean;
  semanticReason?: string | null;
}

/**
 * Wo ein Eintrag dieser Art in der Anwendung liegt.
 *
 * Gegen die Routentabelle geprüft, nicht geraten: für Dokumente gibt es keine
 * Seite, und ein Bericht wird unter /reports/:id/edit geöffnet, nicht unter
 * /reports/:id. Beides hätte den Suchenden auf die Auffangroute geschickt —
 * also zurück aufs Dashboard, ohne zu sagen warum. Was keine Seite hat, bleibt
 * im Ergebnis stehen und ist nur nicht anklickbar: dass etwas gefunden wurde,
 * ist auch dann die Auskunft, die zählt.
 */
const ROUTES: Record<string, (id: string) => string> = {
  finding: (id) => `/findings/${id}`,
  engagement: (id) => `/engagements/${id}`,
  report: (id) => `/reports/${id}/edit`,
  measure: (id) => `/measures/${id}`,
  audit_universe_item: (id) => `/audit-universe/${id}`,
};

/**
 * Der Text eines Treffers, wie ihn ein Mensch lesen soll.
 *
 * Im Index liegt, was der Datensatz trägt: ein Berichtstext als HTML, eine
 * Zusammenfassung mit Markdown-Sternen. Ungefiltert stand in der Trefferliste
 * „<p>Die Interne Revision hat…" und „**3 wesentliche Feststellungen**" — die
 * Auszeichnung gehört in die Ansicht des Datensatzes, nicht in eine Trefferzeile.
 */
export const trefferText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const nackt = value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|li|h\d|tr|div)>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|\s)[*_]{1,2}(\S[^*_]*?)[*_]{1,2}(?=\s|$)/g, '$1$2')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return nackt || undefined;
};

/** Die Seite eines Eintrags dieser Art — null, wenn es keine gibt. */
export const seiteFuer = (entityType: string, id: string): string | null =>
  ROUTES[entityType] ? ROUTES[entityType](id) : null;

/**
 * K12: was eine Frage an den Bestand zurückbringt. Entweder hat die Suche
 * gezählt („count“) oder das Modell hat aus den Treffern geantwortet
 * („answer“). Beides sagt, aus wie vielen Treffern es kommt; die Treffer
 * kommen mit, und die Liste ist maßgeblich, nicht der Absatz.
 */
export interface BestandsAntwort {
  mode: 'count' | 'answer';
  question: string;
  answer: string;
  readableTypes: string[];
  hitsTotal: number;
  hitsConsidered: number;
  counts?: Record<string, number>;
  records: Array<{ key: string; type: string; id: string; title: string; status: string | null }>;
  semantic: boolean;
  semanticReason: string | null;
  refs: string[];
  unknownRefs: string[];
  countClaims: string[];
  flaggedNumbers: string[];
  model: string | null;
  provider: string | null;
  truncated: boolean;
}

/**
 * Die zweite Art von Antwort: gerechnet statt formuliert.
 *
 * Eine Suche antwortet aus Treffern, und Abwesenheit erzeugt keine. Fragen
 * nach dem, was fehlt, beantwortet darum eine benannte Auswertung — die
 * Datenbank rechnet, das Modell hat nur gewählt, welche.
 */
export interface AnalysisAnswer {
  mode: 'analysis';
  key: string;
  name: string;
  /** Die Parameter, mit denen gerechnet wurde — sie werden bei einer Anschlussfrage wieder mitgegeben. */
  parameters: { engagement?: string; year?: number; text?: string };
  /** Die Parameter im Klartext. Steht über der Tabelle, damit ein Missverständnis auffällt. */
  parameterText: string;
  columns: Array<{ key: string; label: string; kind?: 'label' | 'date' | 'code' }>;
  rowType: 'finding' | 'measure';
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  note: string;
}

/**
 * Die dritte Art: eine Anschlussfrage an die Auswertung, die gerade dasteht.
 *
 * „Und wer ist dafür zuständig?" ist ohne die vorige Tabelle keine
 * beantwortbare Frage. Die Grundlage sind darum nicht Treffer einer Suche,
 * sondern die Zeilen der Auswertung — frisch gerechnet, mit denselben Rechten.
 * Gerechnet wird weiterhin nur in der Datenbank; das Modell formuliert über
 * die Zeilen, es zählt sie nicht.
 */
export interface ZeilenAntwort extends Omit<BestandsAntwort, 'mode' | 'counts'> {
  mode: 'rows';
  /** Woher die Zeilen kommen — steht in der Antwort, damit der Bezug sichtbar bleibt. */
  basis: { key: string; name: string; parameterText: string };
}

/** Der Bezug, der mit einer Anschlussfrage hinausgeht: Schlüssel und Parameter, nie Zeilen. */
export interface AskContext {
  key: string;
  engagement?: string;
  year?: number;
  /** Ein Stichwort, auf das die angezeigte Tabelle schon eingeschränkt ist. */
  text?: string;
}

/** Dieselbe Tür, drei Arten von Antwort — unterschieden am `mode`. */
export type AskAnswer = BestandsAntwort | AnalysisAnswer | ZeilenAntwort;

export const searchApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    globalSearch: builder.query<
      SearchResponse,
      { q: string; type?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: '/search',
        params,
      }),
      /**
       * The service answers with `data`, the search bar read `results` — so
       * `data.results.length` threw the moment a search came back, and the
       * dialog went blank. Mapped here rather than renamed there, because the
       * shape the service returns is the same one every other list endpoint
       * uses.
       */
      transformResponse: (response: RawSearchResponse): SearchResponse => ({
        results: (response?.data ?? []).map((hit) => {
          const source = hit.source ?? {};
          return {
            id: hit.id,
            entityType: hit.entityType,
            title:
              trefferText(source.title) ??
              trefferText(source.engagementNumber) ??
              trefferText(source.reportNumber) ??
              'Ohne Titel',
            description: trefferText(source.description) ?? trefferText(source.objective),
            url: ROUTES[hit.entityType] ? ROUTES[hit.entityType](hit.id) : null,
            score: hit.score,
          };
        }),
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        totalPages: response?.totalPages ?? 0,
        semantic: response?.semantic === true,
        semanticReason: response?.semanticReason ?? null,
      }),
      providesTags: ['Search'],
    }),
    searchSuggestions: builder.query<
      { suggestions: Array<{ text: string; entityType: string; id: string }> },
      { q: string }
    >({
      query: (params) => ({
        url: '/search/suggestions',
        params,
      }),
    }),
    /** K12: eine Frage an den Bestand — nichts wird gespeichert, darum eine Mutation ohne Cache. */
    askRecords: builder.mutation<AskAnswer, { question: string; context?: AskContext }>({
      query: (body) => ({ url: '/search/ask', method: 'POST', body }),
    }),
    /** Reads the tenant's data into the index; answers with what was written. */
    reindexAll: builder.mutation<
      { indexed: Record<string, number>; total: number; failed: number },
      void
    >({
      query: () => ({ url: '/search/reindex-all', method: 'POST' }),
      invalidatesTags: ['Search'],
    }),
  }),
});

export const {
  useGlobalSearchQuery,
  useLazyGlobalSearchQuery,
  useSearchSuggestionsQuery,
  useLazySearchSuggestionsQuery,
  useReindexAllMutation,
  useAskRecordsMutation,
} = searchApi;
